import React, { useEffect, useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Tag, Spin, Pagination } from 'antd';
import MyCaseCard from '../MyCaseCard';
import { CaseListItem, ICaseStatus, MyCaseFilter } from 'types/textVisit';
import useGetAllCaseCount from 'hooks/textVisit/useGetAllCaseCount';
import { getMyCaseList } from 'api/textVisit';
import TextVisitSubmittedDrawer from 'components/TextVisitSubmittedDrawer';
import s from './s.module.less';
import { renderEmptyStateForMyCase } from '../AllCasePanel';

interface MyCasePanelProps {
    onResumeCase: (caseData: CaseListItem) => void;
}

const MyCasePanel = forwardRef((props: MyCasePanelProps, ref) => {
    const { onResumeCase } = props;
    const [myCaseTag, setMyCaseTag] = useState<'in-progress' | 'active' | 'closed'>('in-progress');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [myCaseList, setMyCaseList] = useState<CaseListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [allCaseCounts, , refetchAllCaseCount] = useGetAllCaseCount();
    const [selectedCase, setSelectedCase] = useState<CaseListItem>();
    const [isModalOpen, setIsModalOpen] = useState(false);

    // 获取数据并更新总数
    const fetchDataWithTotal = useCallback(async (params: MyCaseFilter) => {
        try {
            setLoading(true);
            const result = await getMyCaseList(params);
            if (!result.error && result?.data?.data) {
                const data: CaseListItem[] = result.data.data.list || [];
                // 更新列表数据
                setMyCaseList(data);
                // 更新总数（API 可能返回 totalRecords 或 total）
                setTotal(result.data.data.totalRecords || result.data.data.total || 0);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch data with total:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // 当标签变化时，重置到第一页并获取数据
    useEffect(() => {
        setCurrentPage(1);
        const params: MyCaseFilter = {
            status: myCaseTag === 'closed' ? ICaseStatus.CLOSED : ICaseStatus.ACTIVE,
            size: pageSize,
            current: 1,
        };
        if (myCaseTag !== 'closed') {
            params.treatmentSubmitted = myCaseTag !== 'in-progress';
        }
        fetchDataWithTotal(params);
    }, [myCaseTag, pageSize, fetchDataWithTotal]);

    // 当页码变化时重新获取数据
    useEffect(() => {
        const params: MyCaseFilter = {
            status: myCaseTag === 'closed' ? ICaseStatus.CLOSED : ICaseStatus.ACTIVE,
            size: pageSize,
            current: currentPage,
        };
        if (myCaseTag !== 'closed') {
            params.treatmentSubmitted = myCaseTag !== 'in-progress';
        }
        fetchDataWithTotal(params);
    }, [currentPage, myCaseTag, pageSize, fetchDataWithTotal]);

    const myCaseTags = [
        { label: `In-progress (${allCaseCounts.inProgressCount})`, key: 'in-progress' },
        { label: `Active (${allCaseCounts.activeCount})`, key: 'active' },
        { label: `Closed (${allCaseCounts.closedCount})`, key: 'closed' },
    ];

    const refetchData = async () => {
        try {
            const params: MyCaseFilter = {
                status: myCaseTag === 'closed' ? ICaseStatus.CLOSED : ICaseStatus.ACTIVE,
                size: pageSize,
                current: currentPage,
            };
            if (myCaseTag !== 'closed') {
                params.treatmentSubmitted = myCaseTag !== 'in-progress';
            }
            await fetchDataWithTotal(params);
            refetchAllCaseCount();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to refetch data:', error);
        }
    };

    // 处理分页变化
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    useImperativeHandle(ref, () => ({
        fetchData: refetchData,
    }));

    return (
        <div>
            <div className={s.myCaseTags}>
                {myCaseTags.map((tag) => (
                    <Tag.CheckableTag
                        checked={myCaseTag === tag.key}
                        key={tag.key}
                        className={s.myCaseTag}
                        onClick={() => setMyCaseTag(tag.key as 'in-progress' | 'active' | 'closed')}
                    >
                        {tag.label}
                    </Tag.CheckableTag>
                ))}
            </div>
            <Spin spinning={loading}>
                {myCaseList.length > 0 ? (
                    <>
                        <div className={s.myCasesGrid}>
                            {myCaseList.map((caseItem) => (
                                <MyCaseCard
                                    key={caseItem.id}
                                    caseData={caseItem}
                                    onRefetch={refetchData}
                                    onViewCase={(caseData) => {
                                        setSelectedCase(caseData);
                                        setIsModalOpen(true);
                                    }}
                                    onResumeCase={(caseData) => {
                                        onResumeCase(caseData);
                                    }}
                                />
                            ))}
                        </div>
                        {total > 0 && (
                            <div className={s.paginationWrapper}>
                                <Pagination
                                    disabled={loading}
                                    hideOnSinglePage
                                    current={currentPage}
                                    total={total}
                                    pageSize={pageSize}
                                    onChange={handlePageChange}
                                />
                            </div>
                        )}
                    </>
                ) : renderEmptyStateForMyCase()}
            </Spin>
            <TextVisitSubmittedDrawer open={isModalOpen} onClose={() => setIsModalOpen(false)} textVisitUuid={selectedCase?.textVisitUuid} />
        </div>
    );
});

export default MyCasePanel;
